import api from './api';

// ── TIPI ────────────────────────────────────────────────────────────────────

export interface Mezzo {
  id: number;
  targa: string;
  marca?: string;
  modello?: string;
  anno?: number;
  tipo?: string;
  tipo_veicolo?: string;
  tipo_proprieta?: string;
  status_veicolo?: string;
  colore?: string;
  telaio?: string;
  cilindrata?: number;
  alimentazione?: string;
  data_immatricolazione?: string;
  data_acquisto?: string;
  valore_acquisto?: number;
  fornitore_nome?: string;
  fine_contratto?: string;
  tecnico_assegnato_id?: number;
  tecnico_assegnato_nome_provvisorio?: string;
  tecnico_nome?: string;
  km?: number;
  stato_carrozzeria?: string;
  note?: string;
  is_active: boolean;
  scadenze_in_scadenza?: number;
}

export interface SpesaMezzo {
  id: number;
  mezzo_id: number;
  mezzo_targa?: string;
  data: string;
  tipo_spesa: string;
  importo: number;
  km_percorsi?: number;
  litri?: number;
  euro_al_litro?: number;
  km_iniziali?: number;
  km_finali?: number;
  km_totali?: number;
  consumo_per_100km?: number;
  fornitore_nome?: string;
  n_fattura?: string;
  data_fattura?: string;
  autista_id?: number;
  autista_nome?: string;
  localita?: string;
  tipo_rifornimento?: string;
  note?: string;
}

export interface Scadenza {
  id: number;
  mezzo_id: number;
  mezzo_targa?: string;
  mezzo_modello?: string;
  tipologia: string;
  data_scadenza: string;
  data_pagamento?: string;
  costo?: number;
  descrizione?: string;
  fornitore_nome?: string;
  allegato_path?: string;
  notifica_giorni_prima: number;
  notifica_inviata: boolean;
  giorni_alla_scadenza?: number;
  stato?: 'ok' | 'in_scadenza' | 'scaduta';
}

export interface Manutenzione {
  id: number;
  mezzo_id: number;
  mezzo_targa?: string;
  mezzo_modello?: string;
  tipologia: string;
  data: string;
  descrizione?: string;
  officina?: string;
  costo?: number;
  km_al_intervento?: number;
  prossimo_intervento_km?: number;
  prossimo_intervento_data?: string;
  note?: string;
  allegato_path?: string;
}

export interface Multa {
  id: number;
  mezzo_id: number;
  mezzo_targa?: string;
  autista_id?: number;
  autista_nome?: string;
  n_verbale?: string;
  localita?: string;
  data_multa: string;
  data_notifica?: string;
  costo: number;
  note?: string;
  pagata: boolean;
  data_pagamento?: string;
  notificata_autista: boolean;
  data_notifica_autista?: string;
  addebitata_autista: boolean;
  data_addebito?: string;
  allegato_path?: string;
}

export interface Carta {
  id: number;
  mezzo_id?: number;
  mezzo_targa?: string;
  autista_id?: number;
  autista_nome?: string;
  tipologia: string;
  intestatario?: string;
  codice?: string;
  limite?: string;
  status: string;
  data_emissione?: string;
  data_scadenza?: string;
  note?: string;
}

export interface IngressoUscita {
  id: number;
  mezzo_id: number;
  mezzo_targa?: string;
  autista_id?: number;
  autista_nome?: string;
  data_uscita: string;
  km_uscita?: number;
  data_rientro?: string;
  km_rientro?: number;
  km_percorsi?: number;
  destinazione?: string;
  note?: string;
}

export interface FleetKpi {
  n_mezzi_attivi: number;
  totale_spese_periodo: number;
  totale_carburante_periodo: number;
  n_scadenze_urgenti: number;
  n_scadenze_scadute: number;
  totale_multe_aperte: number;
  periodo_da: string;
  periodo_a: string;
}

// ── API CALLS ────────────────────────────────────────────────────────────────

// Mezzi
export const getMezzi = (soloAttivi = true) =>
  api.get<Mezzo[]>('/mezzi/extended', { params: { solo_attivi: soloAttivi } });

export const getMezzo = (id: number) =>
  api.get<Mezzo>(`/mezzi/${id}`);

export const createMezzo = (data: Partial<Mezzo>) =>
  api.post<Mezzo>('/mezzi/extended', data);

export const updateMezzo = (id: number, data: Partial<Mezzo>) =>
  api.put<Mezzo>(`/mezzi/extended/${id}`, data);

export const deleteMezzo = (id: number) =>
  api.delete(`/mezzi/${id}`);

export const deleteMezzoDefinitivo = (id: number) =>
  api.delete(`/mezzi/${id}/definitivo`);

// Spese mezzo
export const getSpeseMezzo = (mezzoId: number, params?: Record<string, unknown>) =>
  api.get<SpesaMezzo[]>(`/mezzi/${mezzoId}/spese/extended`, { params });

export const createSpesaMezzo = (mezzoId: number, data: Partial<SpesaMezzo>) =>
  api.post<SpesaMezzo>(`/mezzi/${mezzoId}/spese/extended`, data);

export const updateSpesaMezzo = (mezzoId: number, spesaId: number, data: Partial<SpesaMezzo>) =>
  api.put<SpesaMezzo>(`/mezzi/${mezzoId}/spese/${spesaId}`, data);

export const deleteSpesaMezzo = (mezzoId: number, spesaId: number) =>
  api.delete(`/mezzi/${mezzoId}/spese/${spesaId}`);

// Scadenze
export const getScadenze = (params?: Record<string, unknown>) =>
  api.get<Scadenza[]>('/fleet/scadenze', { params });

export const getScadenzeUrgenti = (giorni = 30) =>
  api.get<Scadenza[]>('/fleet/scadenze/in-scadenza', { params: { giorni } });

export const createScadenza = (data: Partial<Scadenza>) =>
  api.post<Scadenza>('/fleet/scadenze', data);

export const updateScadenza = (id: number, data: Partial<Scadenza>) =>
  api.put<Scadenza>(`/fleet/scadenze/${id}`, data);

export const deleteScadenza = (id: number) =>
  api.delete(`/fleet/scadenze/${id}`);

// Manutenzione
export const getManutenzioni = (params?: Record<string, unknown>) =>
  api.get<Manutenzione[]>('/fleet/manutenzione', { params });

export const createManutenzione = (data: Partial<Manutenzione>) =>
  api.post<Manutenzione>('/fleet/manutenzione', data);

export const updateManutenzione = (id: number, data: Partial<Manutenzione>) =>
  api.put<Manutenzione>(`/fleet/manutenzione/${id}`, data);

export const deleteManutenzione = (id: number) =>
  api.delete(`/fleet/manutenzione/${id}`);

// Multe
export const getMulte = (params?: Record<string, unknown>) =>
  api.get<Multa[]>('/fleet/multe', { params });

export const createMulta = (data: Partial<Multa>) =>
  api.post<Multa>('/fleet/multe', data);

export const updateMulta = (id: number, data: Partial<Multa>) =>
  api.patch<Multa>(`/fleet/multe/${id}`, data);

export const deleteMulta = (id: number) =>
  api.delete(`/fleet/multe/${id}`);

// Carte
export const getCarte = (params?: Record<string, unknown>) =>
  api.get<Carta[]>('/fleet/carte', { params });

export const createCarta = (data: Partial<Carta>) =>
  api.post<Carta>('/fleet/carte', data);

export const updateCarta = (id: number, data: Partial<Carta>) =>
  api.put<Carta>(`/fleet/carte/${id}`, data);

export const deleteCarta = (id: number) =>
  api.delete(`/fleet/carte/${id}`);

// Ingressi/Uscite
export const getIngressiUscite = (params?: Record<string, unknown>) =>
  api.get<IngressoUscita[]>('/fleet/ingressi-uscite', { params });

export const createIngressoUscita = (data: Partial<IngressoUscita>) =>
  api.post<IngressoUscita>('/fleet/ingressi-uscite', data);

export const updateIngressoUscita = (id: number, data: Partial<IngressoUscita>) =>
  api.put<IngressoUscita>(`/fleet/ingressi-uscite/${id}`, data);

export const deleteIngressoUscita = (id: number) =>
  api.delete(`/fleet/ingressi-uscite/${id}`);

// Dashboard
export const getFleetKpi = (params?: { data_da?: string; data_a?: string }) =>
  api.get<FleetKpi>('/fleet/dashboard/kpi', { params });

export const getSpesePerTipo = (params?: Record<string, unknown>) =>
  api.get<{ tipo_spesa: string; totale: number; n_voci: number }[]>(
    '/fleet/dashboard/spese-per-tipo', { params }
  );

export const getSpeseMessili = (anno?: number) =>
  api.get<{ mese: number; tipo_spesa: string; totale: number }[]>(
    '/fleet/dashboard/spese-mensili', { params: { anno } }
  );

export const getSpesePerMezzo = (params?: Record<string, unknown>) =>
  api.get<{ mezzo_id: number; targa: string; modello: string; totale: number }[]>(
    '/fleet/dashboard/spese-per-mezzo', { params }
  );

// ── Export / Import XLSX ──────────────────────────────────────────────────────

export const exportMezziXlsx = (soloAttivi = false) =>
  api.get('/mezzi/export-xlsx', { params: { solo_attivi: soloAttivi }, responseType: 'blob' });

export const importMezziXlsx = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<{ created: number; updated: number; errors: string[] }>('/mezzi/import-xlsx', fd);
};

export const exportSpeseXlsx = (mezzoId: number, params?: Record<string, unknown>) =>
  api.get(`/mezzi/${mezzoId}/spese/export-xlsx`, { params, responseType: 'blob' });

export const importSpeseXlsx = (mezzoId: number, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<{ created: number; updated: number; errors: string[] }>(`/mezzi/${mezzoId}/spese/import-xlsx`, fd);
};

export const exportManutenzioniXlsx = (params?: Record<string, unknown>) =>
  api.get('/fleet/manutenzione/export-xlsx', { params, responseType: 'blob' });

export const importManutenzioniXlsx = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<{ created: number; updated: number; errors: string[] }>('/fleet/manutenzione/import-xlsx', fd);
};

export const exportScadenzeXlsx = (params?: Record<string, unknown>) =>
  api.get('/fleet/scadenze/export-xlsx', { params, responseType: 'blob' });

export const importScadenzeXlsx = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<{ created: number; updated: number; errors: string[] }>('/fleet/scadenze/import-xlsx', fd);
};

export const exportMulteXlsx = (params?: Record<string, unknown>) =>
  api.get('/fleet/multe/export-xlsx', { params, responseType: 'blob' });

export const importMulteXlsx = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<{ created: number; updated: number; errors: string[] }>('/fleet/multe/import-xlsx', fd);
};

export const exportCarteXlsx = (params?: Record<string, unknown>) =>
  api.get('/fleet/carte/export-xlsx', { params, responseType: 'blob' });

export const importCarteXlsx = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<{ created: number; updated: number; errors: string[] }>('/fleet/carte/import-xlsx', fd);
};

export const exportIngressiXlsx = (params?: Record<string, unknown>) =>
  api.get('/fleet/ingressi-uscite/export-xlsx', { params, responseType: 'blob' });

export const importIngressiXlsx = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<{ created: number; updated: number; errors: string[] }>('/fleet/ingressi-uscite/import-xlsx', fd);
};
